import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import { appleVideos, homeFeatured, movieVideos, tvVideos } from '@/mocks/videos';
import HeroCarousel from './components/HeroCarousel';
import CategoryEntries from './components/CategoryEntries';
import AppleShowcase from './components/AppleShowcase';

export default function Home() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const latestFilms = useMemo(
    () =>
      [...movieVideos, ...tvVideos]
        .sort((a, b) => (a.uploadDate < b.uploadDate ? 1 : -1))
        .slice(0, 8),
    [],
  );

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <Navbar />

      <main className="flex-1">
        <HeroCarousel slides={homeFeatured} />

        <CategoryEntries />

        <section className="px-4 py-12 md:px-8 md:py-16" aria-label="最新影视视频">
          <div className="mb-8 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                最新影视视频
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                电影与电视剧混合推荐，按上线时间为你排序。
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/category/movie"
                className="whitespace-nowrap rounded-full border border-background-300 px-4 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                电影
              </Link>
              <Link
                to="/category/tv"
                className="whitespace-nowrap rounded-full border border-background-300 px-4 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                电视剧
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6">
              {Array.from({ length: 8 }).map((_, index) => (
                <div
                  key={index}
                  className="overflow-hidden rounded-lg border border-background-200/80 bg-background-50"
                >
                  <div className="aspect-video w-full mv-animate-pulse bg-background-200" />
                  <div className="space-y-3 p-4">
                    <div className="h-4 w-3/4 rounded mv-animate-pulse bg-background-200" />
                    <div className="h-3 w-full rounded mv-animate-pulse bg-background-200" />
                    <div className="h-3 w-2/3 rounded mv-animate-pulse bg-background-200" />
                  </div>
                </div>
              ))}
            </div>
          ) : latestFilms.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-12 text-center">
              <i className="ri-movie-2-line text-4xl text-foreground-300" aria-hidden="true" />
              <p className="mt-3 text-sm text-foreground-600">
                暂无最新影视视频，推荐内容正在更新中，请稍后再来。
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6" data-product-shop>
              {latestFilms.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}
        </section>

        <AppleShowcase videos={appleVideos} />

        <section className="px-4 py-12 md:px-8 md:py-16" aria-label="平台简介">
          <div className="rounded-lg bg-secondary-900 px-6 py-10 md:px-12 md:py-14">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
              <div>
                <h2 className="font-heading text-2xl font-black text-background-50 md:text-3xl">
                  关于苹果视频
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-background-300 md:text-base">
                  苹果视频专注整合丰富的影视资源与苹果品牌相关视频内容，为影视爱好者与数码产品爱好者提供一个集中浏览、筛选与观看的内容平台。无论是想追一部新电影、追一部热播剧，还是想了解苹果最新产品的表现，你都能在这里快速找到感兴趣的内容。
                </p>
                <Link
                  to="/about"
                  className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  了解平台详情
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: '24+', label: '精选视频内容' },
                  { value: '3', label: '核心内容频道' },
                  { value: '50万+', label: '累计观看次数' },
                  { value: '每日', label: '内容持续更新' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg bg-background-50/5 p-5 text-center"
                  >
                    <p className="font-heading text-2xl font-black text-accent-400 md:text-3xl">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs text-background-300 md:text-sm">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}